import { useNavigate } from "react-router-dom";
import { Hammer, HomeIcon, ArrowRight, ShieldCheck, Zap } from "lucide-react";
import ToolsBackdrop from "@/components/ToolsBackdrop";
import { track } from "@/lib/analytics";

export default function Onboarding() {
  const nav = useNavigate();
  return (
    <div className="app-shell grain-bg tools-bg flex flex-col">
      <ToolsBackdrop />
      <div className="flex-1 px-6 pt-10 pb-6">
        <div className="flex items-center gap-3 mb-6 text-plate">
          <img src="/brand/icon-192.png" alt="Besoin d'un Pro" className="h-14 w-14 rounded-2xl shadow-md" />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate2">Besoin d&apos;un Pro&nbsp;?</div>
            <div className="font-heading font-bold text-ink text-sm">La bonne personne, au bon endroit, au bon moment.</div>
          </div>
        </div>

        <div className="text-plate mb-6">
          <h1 className="font-heading font-bold text-4xl text-ink leading-[1.05] tracking-tight">
            Devis locaux,<br/>
            <span className="text-brand">contacts qualifiés.</span>
          </h1>
          <p className="text-slate2 text-sm leading-relaxed mt-3">
            La marketplace française qui met en relation les particuliers avec des artisans
            vérifiés autour de chez eux.
          </p>
        </div>

        <div className="space-y-4">
          <button
            data-testid="choose-particulier-btn"
            onClick={() => { track("particulier_signup_start", { source: "onboarding" }); nav("/register/particulier"); }}
            className="w-full bg-white border border-border rounded-2xl p-5 text-left tap-scale group"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-light flex items-center justify-center">
                <HomeIcon className="w-6 h-6 text-brand" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate2 mb-1">Je suis</div>
                <h3 className="font-heading font-bold text-xl text-ink">Particulier</h3>
                <p className="text-xs text-slate2 mt-1.5 leading-relaxed">
                  Je cherche un artisan ou un service local. Je crée ma demande et je reçois des devis.
                </p>
                <div className="flex items-center gap-1.5 text-xs text-brand font-semibold mt-3">
                  Commencer gratuitement <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </div>
            </div>
          </button>

          <button
            data-testid="choose-pro-btn"
            onClick={() => { track("pro_signup_start", { source: "onboarding" }); nav("/charter"); }}
            className="w-full bg-pro text-white rounded-2xl p-5 text-left tap-scale"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center">
                <Hammer className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Je suis</div>
                <h3 className="font-heading font-bold text-xl">Professionnel</h3>
                <p className="text-xs text-white/70 mt-1.5 leading-relaxed">
                  Je veux développer mon chiffre d'affaires avec des leads locaux qualifiés.
                </p>
                <div className="flex items-center gap-1.5 text-xs text-white font-semibold mt-3">
                  Devenir Pro <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[11px] uppercase tracking-widest text-slate2">ou</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            data-testid="google-signin-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth-callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white border border-border rounded-2xl p-4 text-center tap-scale flex items-center justify-center gap-3"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.4C29.7 34.5 27 35.5 24 35.5c-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.5 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.4C41.9 35.7 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <span className="text-sm font-semibold text-ink">Continuer avec Google</span>
          </button>
        </div>

        <div className="mt-10 space-y-3">
          <div className="flex items-center gap-3 text-xs text-slate2">
            <ShieldCheck className="w-4 h-4 text-forest" /> Profils vérifiés (téléphone, email, SIRET)
          </div>
          <div className="flex items-center gap-3 text-xs text-slate2">
            <Zap className="w-4 h-4 text-brand" /> Demandes générées en continu dans votre zone
          </div>
        </div>

        <div className="mt-10 text-center text-xs text-slate2">
          Déjà inscrit ?{" "}
          <button onClick={() => nav("/login")} className="text-brand font-semibold" data-testid="goto-login-btn">
            Connectez-vous
          </button>
        </div>
      </div>
    </div>
  );
}
